import { SEOHead } from "components/SEO"
import { generateProductPageGraphSchema } from "utils/structuredData"
import BIToolPoweredByApacheSupersetSection from "components/pages/product/BIToolPoweredByApacheSupersetSection"
import DeploymentCalloutSection from "components/pages/product/DeploymentCalloutSection"
import FeaturesLockupSection from "components/pages/product/FeaturesLockupSection"
import HeroSection from "components/pages/product/HeroSection"
import ProductHighlightsSection from "components/pages/product/ProductHighlightsSection"
import VisualizationsSection from "components/pages/product/VisualizationsSection"
import FAQSection from "components/pages/product/FAQSection"
import DoubleCTASection from "components/ui/Blocks/DoubleCTASection"
import Layout from "components/ui/Layout"

function PresetCloud({ location: { pathname } }) {
  return (
    <Layout
      contentSpaced
      trackingName="Preset Cloud"
      pageName="preset-cloud"
      pagePath={pathname}
    >
      <HeroSection />
      <BIToolPoweredByApacheSupersetSection />
      <VisualizationsSection />
      <ProductHighlightsSection />
      <FeaturesLockupSection />
      <DeploymentCalloutSection />
      <FAQSection />
      <DoubleCTASection noBackground trackingName="Preset Cloud" />
    </Layout>
  )
}

const presetCloudFaqs = [
  {
    question: "What is Preset Cloud?",
    answer:
      "Preset Cloud is a fully managed, multi-tenant cloud service built on Apache Superset. It gives teams enterprise-grade analytics with no infrastructure setup, automatic updates, and built-in security.",
  },
  {
    question:
      "How is Preset Cloud different from self-hosting Apache Superset?",
    answer:
      "Preset Cloud handles infrastructure management, security patches, and version upgrades. It also provides enterprise features like SSO, audit logging, and row-level security.",
  },
  {
    question: "What databases can I connect to with Preset Cloud?",
    answer:
      "Preset Cloud supports 40+ data sources including Snowflake, BigQuery, Redshift, Postgres, MySQL, Databricks, and other databases compatible with SQLAlchemy connectors or native Superset support.",
  },
  {
    question: "Is Preset Cloud secure and compliant?",
    answer:
      "Yes. Preset Cloud is SOC 2 Type II certified. Data is encrypted in transit and at rest, and the platform supports SSO and SAML authentication.",
  },
  {
    question: "Can I embed Preset Cloud dashboards in my application?",
    answer:
      "Yes. Preset Cloud supports embedded analytics through the Embedded SDK and guest token API, allowing interactive dashboards inside applications while maintaining row-level security.",
  },
  {
    question:
      "What's the difference between Preset Cloud and Preset Managed Private Cloud?",
    answer:
      "Preset Cloud runs on Preset shared infrastructure, while Preset Managed Private Cloud deploys the platform in your own AWS, GCP, or Azure account for greater data residency and compliance control.",
  },
  {
    question: "Can I migrate my assets if I switch deployment models?",
    answer:
      "Yes. Preset exports charts, dashboards, and datasets as standard Apache Superset YAML files, making assets portable across supported deployment models.",
  },
]

// Export a Head component for Gatsby Head API
export function Head() {
  const graphSchema = generateProductPageGraphSchema({
    pageUrl: "https://preset.io/preset-cloud/",
    pageTitle:
      "Preset Cloud — Open Analytics Platform Built on Apache Superset | Preset",
    description:
      "Preset Cloud is a fully-hosted cloud service for Apache Superset that helps teams explore data, build dashboards, query with SQL, and manage analytics without maintaining infrastructure.",
    featureList: [
      "Fully-hosted cloud service for Apache Superset",
      "Automatic Superset updates",
      "Multiple workspace deployment",
      "Role-based access control",
      "Row-level security",
      "Dashboard and chart creation",
      "Collaborative SQL editor",
      "40+ data source connectors",
      "SSO support",
      "SOC 2 Type II certified",
    ],
    faqs: presetCloudFaqs,
    offerDescription: "Try Preset Cloud for free.",
    breadcrumbs: [
      { name: "Home", url: "https://preset.io/" },
      { name: "Preset Cloud", url: "https://preset.io/preset-cloud/" },
    ],
  })

  return (
    <SEOHead
      title="Preset Cloud — Open Analytics Platform Built on Apache Superset"
      description="Preset Cloud is a fully managed open analytics platform built on Apache Superset™. Connect to 40+ data sources, build dashboards, explore data with SQL Lab, and embed analytics in your apps."
      structuredData={[graphSchema]}
    />
  )
}

export default PresetCloud
